import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Printer, Ban, DollarSign, LogIn } from "lucide-react";
import api from "../../services/api";
import { openPdfInViewer } from "../PdfViewer";
import { Panel, thCls, tdCls, inputCls, selectCls, Field, btnPrimary, btnGhost, btnDanger, cls, fmtDT } from "../ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const err = (e, f) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : f);
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function Tills() {
  const [data, setData] = useState(null);
  const [opening, setOpening] = useState("100.00");
  const [closeOpen, setCloseOpen] = useState(false);
  const [closed, setClosed] = useState(null);
  const [form, setForm] = useState({ case_number: "", payer_name: "", method: "CASH", amount: "", reference: "", note: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = useCallback(() => {
    api.get("/ujms/tills/current").then((r) => setData(r.data)).catch(() => setData({ till: null }));
  }, []);
  useEffect(() => { load(); }, [load]);

  const signOn = async () => {
    try {
      await api.post("/ujms/tills/sign-on", { opening_amount: parseFloat(opening) || 0 });
      toast.success("Signed on to till");
      setClosed(null);
      load();
    } catch (e) { err(e, "Sign-on failed"); }
  };

  const takePayment = async () => {
    try {
      const r = await api.post("/ujms/tills/payments", { ...form, amount: parseFloat(form.amount) });
      toast.success(`Receipt ${r.data.receipt_number} — ${money(r.data.amount)}`);
      setForm({ case_number: "", payer_name: "", method: "CASH", amount: "", reference: "", note: "" });
      openPdfInViewer(`/ujms/receipts/${r.data.id}/pdf`, `Receipt ${r.data.receipt_number}`);
      load();
    } catch (e) { err(e, "Payment failed"); }
  };

  const voidReceipt = async (rec) => {
    const reason = window.prompt(`Void receipt ${rec.receipt_number} — reason:`, "");
    if (!reason) return;
    try { await api.post(`/ujms/receipts/${rec.id}/void`, { reason }); toast.success("Receipt voided"); load(); }
    catch (e) { err(e, "Void failed"); }
  };

  if (!data) return <div className="text-sm text-slate-400 p-6">Loading till…</div>;

  if (!data.till) {
    return (
      <div className="max-w-md mx-auto mt-12" data-testid="till-signon-card">
        {closed && <ClosedSummary closed={closed} />}
        <Panel title="sign on to a till">
          <div className="p-5 space-y-4">
            <p className="text-[13px] text-slate-500">Open a cashier session to take payments and print receipts. Count your starting cash drawer first.</p>
            <Field label="Opening cash amount" required>
              <input className={inputCls} type="number" step="0.01" min="0" value={opening}
                onChange={(e) => setOpening(e.target.value)} data-testid="till-opening-amount" />
            </Field>
            <button className={cls(btnPrimary, "w-full justify-center")} onClick={signOn} data-testid="till-signon-btn">
              <LogIn className="w-4 h-4" /> Sign On to Till
            </button>
          </div>
        </Panel>
      </div>
    );
  }

  const t = data.till;
  return (
    <div className="space-y-4" data-testid="till-page">
      <div className="ody2-banner flex items-center gap-3 px-3 py-1.5 rounded-t-sm">
        <span className="text-white text-[15px] font-bold" style={{ textShadow: "0 1px 1px rgba(0,0,0,.35)" }}
          data-testid="till-number">{t.till_number} — {t.cashier_name}</span>
        <span className="text-white/80 text-[12px]">Opened {fmtDT(t.opened_at)}</span>
        <div className="ml-auto flex gap-1">
          <button className="ody2-btn-gray" onClick={() => setCloseOpen(true)} data-testid="till-close-btn">Close / Balance Till</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[["Opening", t.opening_amount], ["Cash", data.totals.CASH], ["Check", data.totals.CHECK],
          ["Card", data.totals.CREDIT_CARD], ["Money Order", data.totals.MONEY_ORDER],
          ["Expected Cash in Drawer", data.expected_cash]].map(([k, v]) => (
          <div key={k} className="border border-[#c8d4e0] bg-white rounded-sm px-3 py-2" data-testid={`till-stat-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="text-[11px] text-slate-500">{k}</div>
            <div className="text-[16px] font-bold text-ujms-dark">{money(v)}</div>
          </div>
        ))}
      </div>

      <Panel title="take a payment" testId="till-payment-panel">
        <div className="p-4 flex flex-wrap items-end gap-3">
          <Field label="Case number" required>
            <input className={cls(inputCls, "w-44 font-mono")} value={form.case_number} onChange={set("case_number")}
              placeholder="26CV000123-910" data-testid="pay-case-number" />
          </Field>
          <Field label="Received from" required>
            <input className={cls(inputCls, "w-44")} value={form.payer_name} onChange={set("payer_name")} data-testid="pay-payer" />
          </Field>
          <Field label="Method" required>
            <select className={cls(selectCls, "w-36")} value={form.method} onChange={set("method")} data-testid="pay-method">
              {(data.methods || []).map((m) => <option key={m} value={m}>{m.replace("_", " ")}</option>)}
            </select>
          </Field>
          <Field label="Amount" required>
            <input className={cls(inputCls, "w-28")} type="number" step="0.01" min="0.01" value={form.amount}
              onChange={set("amount")} data-testid="pay-amount" />
          </Field>
          {form.method !== "CASH" && (
            <Field label="Ref / Check #">
              <input className={cls(inputCls, "w-28")} value={form.reference} onChange={set("reference")} data-testid="pay-reference" />
            </Field>
          )}
          <button className={btnPrimary} onClick={takePayment} data-testid="pay-submit-btn">
            <DollarSign className="w-4 h-4" /> Take Payment &amp; Print Receipt
          </button>
        </div>
      </Panel>

      <Panel title={`receipts this session (${(data.receipts || []).length})`} testId="till-receipts-panel">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Receipt</th><th className={thCls}>Time</th><th className={thCls}>Case</th>
            <th className={thCls}>Payer</th><th className={thCls}>Method</th><th className={thCls}>Amount</th><th className={thCls}></th></tr></thead>
          <tbody>
            {(data.receipts || []).map((r) => (
              <tr key={r.id} className={cls("hover:bg-slate-50", r.voided && "opacity-50")} data-testid={`receipt-row-${r.id}`}>
                <td className={cls(tdCls, "font-mono text-xs font-bold")}>
                  {r.receipt_number}{r.voided && <span className="ml-1.5 text-[10px] font-black text-red-700">VOID</span>}
                </td>
                <td className={cls(tdCls, "font-mono text-xs")}>{(r.created_at || "").slice(11, 16)}</td>
                <td className={cls(tdCls, "font-mono text-xs")}>{r.case_number}</td>
                <td className={tdCls}>{r.payer_name}</td>
                <td className={cls(tdCls, "text-xs")}>{r.method.replace("_", " ")}{r.reference ? ` (${r.reference})` : ""}</td>
                <td className={cls(tdCls, "font-bold")}>{money(r.amount)}</td>
                <td className={tdCls}>
                  <div className="flex gap-1">
                    <button className={cls(btnGhost, "px-2 py-0.5 text-xs")} data-testid={`receipt-print-${r.id}`}
                      onClick={() => openPdfInViewer(`/ujms/receipts/${r.id}/pdf`, `Receipt ${r.receipt_number}`)}>
                      <Printer className="w-3.5 h-3.5" /> Print
                    </button>
                    {!r.voided && (
                      <button className={cls(btnGhost, "px-2 py-0.5 text-xs text-red-700")} onClick={() => voidReceipt(r)}
                        data-testid={`receipt-void-${r.id}`}><Ban className="w-3.5 h-3.5" /> Void</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!(data.receipts || []).length && <tr><td colSpan={7} className={tdCls}>No payments taken this session yet.</td></tr>}
          </tbody>
        </table>
      </Panel>

      <CloseTillDialog open={closeOpen} onClose={() => setCloseOpen(false)} expected={data.expected_cash}
        onDone={(summary) => { setCloseOpen(false); setClosed(summary); load(); }} />
    </div>
  );
}

function ClosedSummary({ closed }) {
  return (
    <div className={cls("border rounded-sm px-4 py-3 mb-4 text-[13px]",
      Math.abs(closed.over_short) < 0.005 ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800")}
      data-testid="till-closed-summary">
      <b>{closed.till_number} balanced &amp; closed.</b> Expected {money(closed.expected_cash)} · Counted {money(closed.counted_cash)} ·{" "}
      {Math.abs(closed.over_short) < 0.005 ? "Balanced exactly" : `${closed.over_short > 0 ? "OVER" : "SHORT"} ${money(Math.abs(closed.over_short))}`}
    </div>
  );
}

function CloseTillDialog({ open, onClose, expected, onDone }) {
  const [counted, setCounted] = useState("");
  useEffect(() => { if (open) setCounted(""); }, [open]);
  const run = async () => {
    try {
      const r = await api.post("/ujms/tills/close", { counted_cash: parseFloat(counted) || 0 });
      toast.success(`Till closed — ${r.data.over_short === 0 ? "balanced exactly" : (r.data.over_short > 0 ? "over " : "short ") + money(Math.abs(r.data.over_short))}`);
      onDone(r.data);
    } catch (e) { err(e, "Close failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="till-close-dialog">
        <DialogHeader><DialogTitle>Close / Balance Till</DialogTitle></DialogHeader>
        <p className="text-[13px] text-slate-600">Count the cash drawer. Expected cash (opening + cash payments): <b>{money(expected)}</b></p>
        <Field label="Counted cash in drawer" required>
          <input className={inputCls} type="number" step="0.01" min="0" value={counted}
            onChange={(e) => setCounted(e.target.value)} data-testid="till-counted-cash" />
        </Field>
        <DialogFooter>
          <button className={btnGhost} onClick={onClose}>Cancel</button>
          <button className={btnDanger} onClick={run} data-testid="till-close-confirm">Close Till</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
